import React, {
  useCallback,
  useRef,
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
} from 'react';
import { useFocusEffect } from '@react-navigation/native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  findNodeHandle,
  Keyboard,
  KeyboardEvent,
  ScrollView,
  TextInput,
  UIManager,
  View,
} from 'react-native';
import { default as CustomScrollView } from '../ScrollView/ScrollView';
import { KeyboardShiftProps } from './KeyboardShift.types';
import {
  IS_IOS,
  KEYBOARD_OPEN_TIME,
  CONTENT_VIEW_STYLE,
  DEFAULT_TAB_BAR_HEIGHT,
} from './KeyboardShift.content';

const measureElement = <T extends View | null>(
  ref: T,
): Promise<{
  x: number;
  y: number;
  width: number;
  height: number;
  pageX: number;
  pageY: number;
}> =>
  new Promise((resolve) =>
    ref?.measure((x, y, width, height, pageX, pageY) =>
      resolve({ x, y, width, height, pageX, pageY }),
    ),
  );

/**
 * Focuses the input when keyboard gets open.
 * It pushes the input upward if it is behind the keyboard.
 * Could be used combined with KeyboardSpacer.
 * Also dismisses the keyboard if user taps outside focused area.
 */
function KeyboardShift(
  {
    addSpace = IS_IOS,
    isInsideTabBar = true,
    additionalOffset = 20,
    contentViewStyle = CONTENT_VIEW_STYLE,
    onKeyboardShow,
    onKeyboardHide,
    children,
    ...rest
  }: KeyboardShiftProps,
  ref: ForwardedRef<ScrollView>,
) {
  const scrollViewRef = useRef<ScrollView>(null);
  const contentContainerRef = useRef<View>(null);
  const keyboardScreenY = useRef(0);
  const sharedKeyboardHeight = useSharedValue(0);

  const animatedKeyboardHeight = useAnimatedStyle(() => ({
    height: sharedKeyboardHeight.value,
  }));

  const updateScrollPosition = useCallback(() => {
    const focusedInput = TextInput.State.currentlyFocusedInput();
    const handle = focusedInput ? findNodeHandle(focusedInput) : null;

    if (!handle || !scrollViewRef.current) {
      return;
    }

    const scrollViewResponder = scrollViewRef.current.getScrollResponder();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Unreachable code error
    UIManager.viewIsDescendantOf(
      handle,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: Unreachable code error
      scrollViewResponder.getInnerViewNode(),
      async (isAncestor: boolean) => {
        if (!isAncestor) {
          return;
        }

        const [scrollViewPos, contentContainerPos, inputPos] =
          await Promise.all([
            measureElement(scrollViewRef.current as never),
            measureElement(contentContainerRef.current),
            measureElement(focusedInput),
          ]);
        const scrollY = scrollViewPos.pageY - contentContainerPos.pageY;
        const targetInputY =
          keyboardScreenY.current - inputPos.height - additionalOffset;
        const scrollDistance = inputPos.pageY - targetInputY;

        if (scrollDistance < 0) {
          return;
        }

        if (IS_IOS) {
          scrollViewRef.current?.scrollTo({
            x: 0,
            y: scrollDistance + scrollY,
            animated: true,
          });
        } else {
          // On Android the OS scrolls automatically to the bottom end of the input
          // So we only have to scroll with the additional offset
          scrollViewRef.current?.scrollTo({
            x: 0,
            y: scrollY + additionalOffset,
            animated: true,
          });
        }
      },
    );
  }, [additionalOffset, keyboardScreenY, scrollViewRef]);

  const handleKeyboardShow = useCallback(
    (event: KeyboardEvent) => {
      const tabBarAdjustment = isInsideTabBar ? DEFAULT_TAB_BAR_HEIGHT : 0;
      const keyboardHeight = event.endCoordinates.height - tabBarAdjustment;

      keyboardScreenY.current = event.endCoordinates.screenY;

      sharedKeyboardHeight.value = withTiming(
        Math.max(sharedKeyboardHeight.value, keyboardHeight),
      );

      setTimeout(
        updateScrollPosition,
        KEYBOARD_OPEN_TIME && KEYBOARD_OPEN_TIME + 50,
      );

      onKeyboardShow?.();
    },
    [
      isInsideTabBar,
      sharedKeyboardHeight,
      updateScrollPosition,
      onKeyboardShow,
    ],
  );

  const handleKeyboardHide = useCallback(() => {
    sharedKeyboardHeight.value = withTiming(0);

    onKeyboardHide?.();
  }, [onKeyboardHide, sharedKeyboardHeight]);

  useFocusEffect(
    useCallback(() => {
      const showSubscription = Keyboard.addListener(
        IS_IOS ? 'keyboardWillShow' : 'keyboardDidShow',
        handleKeyboardShow,
      );
      const hideSubscription = Keyboard.addListener(
        IS_IOS ? 'keyboardWillHide' : 'keyboardDidHide',
        handleKeyboardHide,
      );

      return () => {
        handleKeyboardHide();
        showSubscription.remove();
        hideSubscription.remove();
      };
    }, [handleKeyboardHide, handleKeyboardShow]),
  );

  useImperativeHandle(ref, () => {
    if (!scrollViewRef.current) {
      throw new Error('ScrollView reference is missing');
    }

    return scrollViewRef.current;
  });

  return (
    <CustomScrollView
      ref={scrollViewRef}
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps="handled"
      {...rest}
    >
      <View
        ref={contentContainerRef}
        style={contentViewStyle}
        collapsable={false}
      >
        {children}
        {addSpace ? <Animated.View style={animatedKeyboardHeight} /> : null}
      </View>
    </CustomScrollView>
  );
}

export default forwardRef(KeyboardShift);
